import { useState, useEffect, useRef } from "react"
import { getJobById } from "./api"
import { SHORT_POLL_INTERVAL } from "@ciq/shared/api/api"

/**
 * @typedef {import('./api').JobStatus} JobStatus
 */

/**
 * @typedef {Object} UseJobPollingResult
 * @property {JobStatus|null} job - Current job status
 * @property {boolean} isPolling - Boolean indicating if polling is active
 * @property {string|null} error - Error message if polling failed
 */

const TERMINAL_STATUSES = ["COMPLETED", "ERROR", "CANCELLED"]

/**
 * Hook to poll job status until terminal state.
 * Polls every 5 seconds (SHORT_POLL_INTERVAL) until job reaches COMPLETED, ERROR, or CANCELLED.
 *
 * @param {string} schoolId - School ID
 * @param {string|null} jobId - Job ID to poll (null to skip polling)
 * @returns {UseJobPollingResult} Hook result with job status, polling state, and error
 */
export function useJobPolling(schoolId, jobId) {
  const [job, setJob] = useState(null)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState(null)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (!jobId) {
      setJob(null)
      setIsPolling(false)
      setError(null)
      return
    }

    let isMounted = true

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (isMounted) {
        setIsPolling(false)
      }
    }

    const poll = async () => {
      if (!schoolId || !jobId) return

      try {
        const result = await getJobById(schoolId, jobId)

        if (!isMounted) return

        if (!result) {
          setError("Job not found")
          stopPolling()
          return
        }

        setJob(result)

        if (TERMINAL_STATUSES.includes(result.status)) {
          stopPolling()
        }
      } catch (err) {
        if (!isMounted) return
        setError(err.message || "Failed to fetch job status")
        stopPolling()
      }
    }

    setIsPolling(true)
    setError(null)

    // Initial poll
    poll()

    // Set up interval using shared constant
    intervalRef.current = setInterval(poll, SHORT_POLL_INTERVAL)

    return () => {
      isMounted = false
      stopPolling()
    }
  }, [schoolId, jobId])

  return { job, isPolling, error }
}
